import { KEY_STORAGE, MEMBER_RANKING_TYPE, ServicePointsType, removeAllStorage } from 'constants/common'
import { PACKAGE, PACKAGE_ADDONS, PACKAGE_TYPE, PackageItem } from 'constants/package'
import { colors } from 'constants/theme'
import CryptoJS from 'crypto-js'
import _ from 'lodash'
import moment from 'moment'
import { ChangeEvent, } from 'react'
import Cookies from 'universal-cookie'
import { getSecureItem, setSecureItem, removeSecureItem, getStorageSecret } from 'utils/storage'

export { getSecureItem, setSecureItem, removeSecureItem } from 'utils/storage'

export const getServicePointsStorage = (type: 'all' | 'branch' | 'servicePoint') => {
  const servicePoint = getSecureItem(KEY_STORAGE.SERVICE_POINT)
  if (servicePoint) {
    const data: ServicePointsType = JSON.parse(servicePoint)
    if (type === 'branch') return data.branch
    else if (type === 'servicePoint') return data.servicePoint
    else return data
  }
}


export const getPermissions = () => {
  const permissions = getSecureItem(KEY_STORAGE.PERMISSION)
  if (permissions) return JSON.parse(permissions)
}

export const getToken = () => getSecureItem(KEY_STORAGE.TOKEN)

export const getSessionExpired = () => getSecureItem(KEY_STORAGE.SESSION_EXPIRED)

export const getTokenExpiresAt = () => getSecureItem(KEY_STORAGE.TOKEN_EXPIRES_AT)

export const setTokenExpiresAt = (expiresAt: string) => {
  setSecureItem(KEY_STORAGE.TOKEN_EXPIRES_AT, expiresAt)
}

export const getRefreshToken = () => getSecureItem(KEY_STORAGE.REFRESH_TOKEN)

export const setRefreshToken = (refreshToken: string) => {
  setSecureItem(KEY_STORAGE.REFRESH_TOKEN, refreshToken)
}

export const getRefreshTokenExpiresAt = () => getSecureItem(KEY_STORAGE.REFRESH_TOKEN_EXPIRES_AT)

export const setRefreshTokenExpiresAt = (expiresAt: string) => {
  setSecureItem(KEY_STORAGE.REFRESH_TOKEN_EXPIRES_AT, expiresAt)
}

export const isRefreshTokenExpired = (): boolean => {
  const expiresAt = getRefreshTokenExpiresAt()
  if (!expiresAt) return false

  const now = moment()
  const expiration = moment(expiresAt, 'YYYY-MM-DD HH:mm:ss')
  return now.isAfter(expiration)
}

export const isTokenExpired = (): boolean => {
  const expiresAt = getTokenExpiresAt()
  if (!expiresAt) return false // ถ้าไม่มี expiration date ให้ถือว่าไม่หมดอายุ (backward compatibility)

  const now = moment()
  const expiration = moment(expiresAt, 'YYYY-MM-DD HH:mm:ss')
  return now.isAfter(expiration)
}

export const getTokenExpirationTime = (): number | null => {
  const expiresAt = getTokenExpiresAt()
  if (!expiresAt) return null

  const now = moment()
  const expiration = moment(expiresAt, 'YYYY-MM-DD HH:mm:ss')
  const diff = expiration.diff(now, 'milliseconds')
  return diff > 0 ? diff : 0
}

export const getBranch = () => _.get(getServicePointsStorage('branch'), 'id') || 0

export const getBranchName = () => _.get(getServicePointsStorage('branch'), 'name')

export const getServicePoint = () => _.get(getServicePointsStorage('servicePoint'), 'id') || 0

export const getServicePointName = () => _.get(getServicePointsStorage('servicePoint'), 'name')

export const getPermissionShortcuts = () => _.get(getPermissions(), 'shortcuts')

export const getPermissionMenus = () => _.get(getPermissions(), 'menus')

export const getPermissionActiveMenu = () => _.get(getPermissions(), 'activeMenu')

export const getClinicInfo = () => {
  const info = getSecureItem(KEY_STORAGE.CLINIC_INFO)
  if (info) {
    const parsed = JSON.parse(info)
    return parsed
  }
}

export const logout = async (): Promise<void> => {
  // Log logout to backend before clearing storage
  try {
    const LoginApi = (await import('api/login-api')).default
    await LoginApi.logout()
  } catch (error) {
    console.error('Failed to log logout:', error)
    // Continue with logout even if API call fails
  }

  removeAllStorage()
  removeSecureItem(KEY_STORAGE.REFRESH_TOKEN)
  removeSecureItem(KEY_STORAGE.REFRESH_TOKEN_EXPIRES_AT)
  const cookies = new Cookies()
  cookies.remove('REMEMBER_ME')
  cookies.remove('TOKEN')
}

export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = getRefreshToken()
    if (!refreshToken || isRefreshTokenExpired()) {
      return null
    }

    const LoginApi = (await import('api/login-api')).default
    const { accessToken, tokenExpiresAt } = await LoginApi.refreshToken(refreshToken)

    // Update access token and expiration
    setSecureItem(KEY_STORAGE.TOKEN, accessToken)
    if (tokenExpiresAt) {
      setTokenExpiresAt(tokenExpiresAt)
    }

    return accessToken
  } catch (error) {
    console.error('Failed to refresh access token:', error)
    return null
  }
}

export const getMember = () => {
  const members = getSecureItem(KEY_STORAGE.MEMBER_RANKING_TYPE)
  if (members) return JSON.parse(members)
}

export const numberFormat = (num: any, minDigits = 2, maxDigits = 2) => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits
  })
  return formatter.format(num)
}

export const numberNonDigit = (number: any) => {
  const format = Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

  return format.format(number)
}

export const getBoolean = (value: any) => {
  switch (value) {
    case true:
    case 'true':
    case 1:
    case '1':
    case 'on':
    case 'yes':
      return true
    default:
      return false
  }
}

export const padCode = (length: number, number: any, prefix = ``) => prefix + number.toString().padStart(length, '0')

export const memberTypeOutline = (types: string) => {
  const type = types ? types.toLocaleLowerCase() : MEMBER_RANKING_TYPE.BRONZE
  if (type === MEMBER_RANKING_TYPE.SILVER) return colors.memberRanking.rankingSilver
  else if (type === MEMBER_RANKING_TYPE.GOLD) return colors.memberRanking.rankingGold
  else if (type === MEMBER_RANKING_TYPE.PLATINUM) return colors.memberRanking.rankingPlatinum
  else if (type === MEMBER_RANKING_TYPE.DIAMOND) return colors.memberRanking.rankingDiamond
  else return colors.memberRanking.rankingBronze
}

export const memberTypeLabel = (types: string) => {
  const type = types ? types.toLocaleLowerCase() : MEMBER_RANKING_TYPE.BRONZE
  if (type === MEMBER_RANKING_TYPE.SILVER) return colors.memberRanking.background.rankingSilver
  else if (type === MEMBER_RANKING_TYPE.GOLD) return colors.memberRanking.background.rankingGold
  else if (type === MEMBER_RANKING_TYPE.PLATINUM) return colors.memberRanking.background.rankingPlatinum
  else if (type === MEMBER_RANKING_TYPE.DIAMOND) return colors.memberRanking.background.rankingDiamond
  else return colors.memberRanking.background.rankingBronze
}

export const toBuddhistYear = (date: any, format = 'DD/MM/YYYY') => {
  const localLang = getSecureItem(KEY_STORAGE.LANGUAGE) || "TH"
  const era = localLang === 'TH' ? 543 : 0;
  const christianYear = date.format('YYYY')
  const buddhishYear = (parseInt(christianYear) + era).toString()
  if (format === 'DD/MM/YYYY') return date.format('DD/MM/') + buddhishYear.toString()
  else if (format === 'DD/MM/YYYY HH:mm') return `${date.format('DD/MM/')}${buddhishYear.toString()} ${date.format('HH:mm')}`
  else if (format === 'DD/MM/YYYY HH.mm') return `${date.format('DD/MM/')}${buddhishYear.toString()} ${date.format('HH.mm')}`
  else return date.format(format.replace('YYYY', buddhishYear).replace('YY', buddhishYear.substring(2, 4))).replace(christianYear, buddhishYear)
}

export const isBuddhistYear = () => {
  const localLang = getSecureItem(KEY_STORAGE.LANGUAGE) || "TH"
  const era = localLang === 'TH' ? 543 : 0;
  return era
}

export const numberToCurrencyFormat = (number: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'

    // These options are needed to round to whole numbers if that's what you want.
    // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    // maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  })
    .format(number)
    .replace('$', '')
}

export const renderAge = (date: Date | null | string, unit: 'year' | 'month' | 'day') => {
  const now = moment()
  const bd = moment(date)
  if (unit === 'year') return now.diff(bd, 'year')
  if (unit === 'month') return now.diff(bd.add(now.diff(bd, 'year'), 'years'), 'months')
  if (unit === 'day') return now.diff(bd.add(now.diff(bd.add(now.diff(bd, 'year'), 'years'), 'months'), 'months'), 'days')
}

export const getApp = () => {
  const raw: string = getSecureItem(KEY_STORAGE.APP) || ''
  if (!raw) return ''
  // Legacy: value may be stored without ENC_ prefix (old CryptoJS ciphertext) or as plain text
  if (!raw.startsWith('ENC:')) {
    try {
      const dec = CryptoJS.AES.decrypt(raw, getStorageSecret()).toString(CryptoJS.enc.Utf8)
      if (dec) return dec
    } catch {
      // Not valid ciphertext (e.g. plain text "PERMISS"/"UNPERMISS") – return as-is
    }
  }
  return raw
}

export const getCrm = () => {
  const raw: string = getSecureItem(KEY_STORAGE.CRM) || ''
  if (!raw) return false
  if (!raw.startsWith('ENC:')) {
    try {
      const dec = CryptoJS.AES.decrypt(raw, getStorageSecret()).toString(CryptoJS.enc.Utf8)
      if (dec) return dec === 'PERMISS'
    } catch {
      // Not valid ciphertext (e.g. plain text) – treat as raw
    }
  }
  return raw === 'PERMISS'
}

export const getPackage = (): PackageItem => {
  const raw: string = getSecureItem(KEY_STORAGE.PACKAGE) || ''
  const defaultItem: PackageItem = {
    time: '',
    package: PACKAGE_TYPE.STANDARD,
    packageVersion: '',
    packageExpired: '',
    countBranch: 1,
    enabledApp: 0
  }
  if (!raw) return defaultItem
  try {
    // New format: already decrypted by getSecureItem (ENC_ prefix was used when saving)
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.package !== 'undefined') return parsed as PackageItem
  } catch {
    // Legacy: encrypted string without ENC_ prefix (or plain text – decrypt may throw)
    try {
      const dec = CryptoJS.AES.decrypt(raw, getStorageSecret()).toString(CryptoJS.enc.Utf8)
      if (dec) {
        try {
          return JSON.parse(dec) as PackageItem
        } catch {
          // ignore
        }
      }
    } catch {
      // Not valid ciphertext – return default
    }
  }
  return defaultItem
}

export const getPackageKey = () => { return getPackage()?.package || PACKAGE_TYPE.STANDARD }

export const getPackageTime = () => { return getPackage()?.time || '' }

export const getPackageExpired = () => { return getPackage()?.packageExpired || '' }

export const getCountBranch = () => { return getPackage()?.countBranch || 1 }

export const getEnabledApp = () => { return getPackage()?.enabledApp || 0 }

const WEB_MANAGE_ADDONS = [
  PACKAGE_ADDONS.ADDON_BANNER,
  PACKAGE_ADDONS.ADDON_ABOUT_US,
  PACKAGE_ADDONS.ADDON_SERVICE,
  PACKAGE_ADDONS.ADDON_PRICE_LIST,
  PACKAGE_ADDONS.ADDON_PRICE_RANGE,
  PACKAGE_ADDONS.ADDON_DCC,
  PACKAGE_ADDONS.ADDON_REQUIRE_FINANCE_ACKNOWLEDGE,
  PACKAGE_ADDONS.ADDON_NEW_OLD_PATIENT,
  PACKAGE_ADDONS.ADDON_ALERT_NO_TREATMENT,
  PACKAGE_ADDONS.ADDON_ALERT_NO_MEDICINE,
  PACKAGE_ADDONS.ADDON_ALERT_TEETH_BEFORE_PAYMENT,
  PACKAGE_ADDONS.ADDON_TREATMENT_FEE_STATEMENT,
  PACKAGE_ADDONS.ADDON_REVIEW,
  PACKAGE_ADDONS.ADDON_ARTICLE,
  PACKAGE_ADDONS.ADDON_CONTACT_US,
  PACKAGE_ADDONS.ADDON_WEB_MANAGE
]

/** รายการ addon ที่ผู้ใช้ปิด (รูปแบบเก่า - ใช้สำหรับ backward compat) */
export const getAddonDisabledList = (): string[] => {
  try {
    const raw = localStorage.getItem(KEY_STORAGE.ADDON_DISABLED_LIST)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export const setAddonDisabledList = (list: string[]) => {
  localStorage.setItem(KEY_STORAGE.ADDON_DISABLED_LIST, JSON.stringify(list))
}

/** การตั้งค่า addon แบบเปิดปิด + กำหนดวันหมดอายุ + แยกสาขาได้ (เก็บใน localStorage) */
export interface AddonBranchSetting {
  branchId: number
  dateExpired: string | null
  /** เปิดใช้งาน addon ที่สาขานี้ (ถ้า false จะไม่ส่ง branchId ไปใน API = ไม่เปิดที่สาขานี้) */
  enabled?: boolean
}

export interface AddonSetting {
  enabled: boolean
  useAllBranches: boolean
  dateExpiredAll: string | null
  branches: AddonBranchSetting[]
}

export type AddonSettings = Record<string, AddonSetting>

export const getAddonSettings = (): AddonSettings => {
  try {
    const raw = localStorage.getItem(KEY_STORAGE.ADDON_SETTINGS)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

export const setAddonSettings = (settings: AddonSettings) => {
  localStorage.setItem(KEY_STORAGE.ADDON_SETTINGS, JSON.stringify(settings))
}

/** ข้อความท้าย PDF รายงานค่าตอบแทนทันตแพทย์หัก Clinic Fee (กำหนดได้ที่ Addon) */
export const REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT =
  'ขอให้ทันตแพทย์ทุกท่านตรวจสอบความความถูกต้องของข้อมูลกับมาตรการใดมาตรการหนึ่งของทางคลินิกอย่างเคร่งครัด เพื่อป้องกันการทุจริต'

export const getRemunerationClinicFeePdfFooter = (): string => {
  try {
    const raw = localStorage.getItem(KEY_STORAGE.REMUNERATION_CLINIC_FEE_PDF_FOOTER)
    return typeof raw === 'string' && raw.trim() !== '' ? raw : REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT
  } catch {
    return REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT
  }
}

export const setRemunerationClinicFeePdfFooter = (text: string) => {
  localStorage.setItem(KEY_STORAGE.REMUNERATION_CLINIC_FEE_PDF_FOOTER, text)
}

export const getRemunerationClinicFeePdfFooterEnabled = (): boolean => {
  try {
    const raw = localStorage.getItem(KEY_STORAGE.REMUNERATION_CLINIC_FEE_PDF_FOOTER_ENABLED)
    if (raw === '0' || raw === 'false') return false
    return true
  } catch {
    return true
  }
}

export const setRemunerationClinicFeePdfFooterEnabled = (enabled: boolean) => {
  localStorage.setItem(KEY_STORAGE.REMUNERATION_CLINIC_FEE_PDF_FOOTER_ENABLED, enabled ? '1' : '0')
}

export interface RemunerationClinicFeePdfFooterStyle {
  fontSize?: number
  fontWeight?: number
  fontStyle?: 'normal' | 'italic'
  textAlign?: 'left' | 'center' | 'right'
  color?: string
}

const REMUNERATION_CLINIC_FEE_PDF_FOOTER_STYLE_DEFAULT: RemunerationClinicFeePdfFooterStyle = {
  fontSize: 10,
  fontWeight: 500,
  fontStyle: 'normal',
  textAlign: 'right',
  color: '#000000'
}

function isValidHexColor(s: unknown): s is string {
  if (typeof s !== 'string') return false
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)
}

export const getRemunerationClinicFeePdfFooterStyle = (): RemunerationClinicFeePdfFooterStyle => {
  try {
    const raw = localStorage.getItem(KEY_STORAGE.REMUNERATION_CLINIC_FEE_PDF_FOOTER_STYLE)
    if (!raw) return { ...REMUNERATION_CLINIC_FEE_PDF_FOOTER_STYLE_DEFAULT }
    const parsed = JSON.parse(raw) as RemunerationClinicFeePdfFooterStyle
    return {
      ...REMUNERATION_CLINIC_FEE_PDF_FOOTER_STYLE_DEFAULT,
      ...(typeof parsed.fontSize === 'number' ? { fontSize: parsed.fontSize } : {}),
      ...(typeof parsed.fontWeight === 'number' ? { fontWeight: parsed.fontWeight } : {}),
      ...(parsed.fontStyle === 'normal' || parsed.fontStyle === 'italic' ? { fontStyle: parsed.fontStyle } : {}),
      ...(parsed.textAlign === 'left' || parsed.textAlign === 'center' || parsed.textAlign === 'right' ? { textAlign: parsed.textAlign } : {}),
      ...(isValidHexColor(parsed.color) ? { color: parsed.color } : {})
    }
  } catch {
    return { ...REMUNERATION_CLINIC_FEE_PDF_FOOTER_STYLE_DEFAULT }
  }
}

export const setRemunerationClinicFeePdfFooterStyle = (style: RemunerationClinicFeePdfFooterStyle) => {
  localStorage.setItem(KEY_STORAGE.REMUNERATION_CLINIC_FEE_PDF_FOOTER_STYLE, JSON.stringify(style))
}

export const getAddonSetting = (key: string): AddonSetting | undefined => getAddonSettings()[key]

/** เช็คว่า addon นี้เปิดอยู่และยังไม่หมดอายุ (ตามสาขาที่เลือกอยู่) · ถ้ายังไม่เคยตั้งค่า default เปิด */
function isAddonActive(key: string): boolean {
  const setting = getAddonSetting(key)
  const currentBranchId = Number(getBranch()) || 0

  // ถ้ายังไม่มีการตั้งค่า addon นี้ ให้ถือว่าเปิดใช้งาน (default active)
  if (!setting) return false

  if (!setting.enabled) return false
  if (setting.useAllBranches) {
    if (!setting.dateExpiredAll) return true
    return moment(setting.dateExpiredAll).isSameOrAfter(moment(), 'day')
  }
  const branch = _.find(setting.branches, (b) => Number(b.branchId) === currentBranchId)
  if (!branch || branch.enabled === false) return false
  if (!branch.dateExpired) return true
  return moment(branch.dateExpired).isSameOrAfter(moment(), 'day')
}

export const checkPackage = (key: string) => {
  const clientPackage: PACKAGE_TYPE = getPackageKey() || PACKAGE_TYPE.STANDARD
  let packageAddon: string[] = []
  if (getEnabledApp()) packageAddon = [PACKAGE_ADDONS.ADDON_REGISTER_APP]
  const info = getClinicInfo()
  if (info?.isUseWeb === '1') packageAddon = [...packageAddon, ...WEB_MANAGE_ADDONS]
  if (key) return _.includes([...PACKAGE[clientPackage], ...packageAddon], key)
  else return true
}

export const checkAddons = (key: string) => {
  if (!key) return true
  return isAddonActive(key)
}

/**
 * ดึงการตั้งค่า addon (feature configs) จาก API แล้ว merge เฉพาะสาขาที่เข้าระบบลง localStorage
 * ใช้เมื่อโหลดแอปหรือ refresh clinic info เพื่อให้ checkPackage() สอดคล้องกับ backend
 * ใช้ dynamic import เพื่อหลีกเลี่ยง circular dependency (api/api -> app.utils -> about.api -> api/api)
 */
export const syncAddonSettingsFromApi = (): Promise<void> => {
  const currentBranchId = Number(getBranch()) || 0
  if (!currentBranchId) return Promise.resolve()

  return import('api/admin/clinic-manage/about.api').then(({ default: AboutUs }) =>
    AboutUs.findAboutUs()
      .then((res) => {
        const data = res?.data
        const aboutUsId = data?.aboutUsId != null ? Number(data.aboutUsId) : null
        const branches = data?.branchDefaultRegisterApps || []
        if (aboutUsId == null || !branches.length) return null
        return AboutUs.getFeatureConfigs(aboutUsId).then((cfgRes) => ({
          configs: cfgRes?.data || {},
          branchIds: branches.map((b: any) => Number(b.branchId))
        }))
      })
      .then((payload) => {
        if (!payload || Object.keys(payload.configs).length === 0) return
        const existing = getAddonSettings()
        const merged: AddonSettings = {}
        Object.keys(payload.configs).forEach((key) => {
          const item = payload.configs[key]
          const existingSetting = existing[key]
          const baseBranches: AddonBranchSetting[] = existingSetting?.branches?.length
            ? existingSetting.branches
            : payload.branchIds.map((id: number) => ({ branchId: id, dateExpired: null as string | null, enabled: true }))
          const fromApiBranch = (item.branches || []).find((b: any) => Number(b.branchId) === currentBranchId)
          const branchesMerged: AddonBranchSetting[] = baseBranches.map((b) =>
            Number(b.branchId) === currentBranchId
              ? { branchId: b.branchId, dateExpired: fromApiBranch?.dateExpired ?? null, enabled: !!fromApiBranch }
              : b
          )
          merged[key] = {
            enabled: !!item.enabled,
            useAllBranches: !!item.useAllBranches,
            dateExpiredAll: item.dateExpiredAll ?? null,
            branches: branchesMerged
          }
        })
        setAddonSettings({ ...existing, ...merged })
      })
  ).catch(() => undefined)
}

export const numberOnly = (e: ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  // Remove any non-numeric characters from the input
  const numericValue = value.replace(/[^0-9.]/g, '');
  const dotCount = numericValue.split('.').length - 1;
  const sanitizedValue = dotCount <= 1 ? numericValue : numericValue.slice(0, -1);
  return sanitizedValue
};

export const numberOnlyDecimal = (str: any) => {
  let s = String(str).replace(/[^0-9.]/g, '')
  const parts = s.split('.')
  if (parts.length > 1) {
    s = parts.shift() + '.' + parts.join('')
  }
  return s
}

export const stripLeadingZeros = (str: any) => {
  const [intPart, fracPart] = String(str).split('.')
  let i = intPart.replace(/^0+/, '')
  if (i === '') i = '0'
  return fracPart != null ? `${i}.${fracPart}` : i
}


export const insertDashesIphone = (numberIphone: string) => {
  const result = [];
  let index = 0;
  while (index < numberIphone.length) {
    if (index === 0) {
      result.push(numberIphone.substring(index, index + 1));
    } else if (index === 2 || index === 6 || index === 10) {
      result.push('-');
      result.push(numberIphone.substring(index, index + 1));
    } else {
      result.push(numberIphone.substring(index, index + 1));
    }
    index++;
  }
  return result.join('');
}

export const insertDashesText = (text?: string) => {
  if (!text) {
    return ""; // Handle empty string case
  }

  const result = [];
  let add = 80;
  let index = 0;

  while (index < text.length) {
    if (index === add) {
      result.push(text);
    }
    index++;
    add = add + 80
  }

  return result.join(' ');
};
